/**
 * Utility functions for converting angle measurements
 */

/**
 * Converts radians to degrees
 * @param radians - Angle in radians
 * @returns Angle in degrees
 */
export function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Checks if a metric key represents an angle in radians
 * @param key - The metric key to check
 * @returns True if the key represents an angle in radians
 */
export function isRadianMetric(key: string): boolean {
  return key.endsWith('_rad');
}

/**
 * Formats an angle value with appropriate decimal places
 * @param degrees - Angle in degrees
 * @param decimalPlaces - Number of decimal places (default: 1)
 * @returns Formatted angle string with degree symbol
 */
export function formatDegrees(degrees: number, decimalPlaces: number = 1): string {
  return `${degrees.toFixed(decimalPlaces)}°`;
}

/**
 * Converts a metric name from radians to degrees notation
 * @param metricName - The original metric name with "(rad)" notation
 * @returns The metric name with "(°)" notation
 */
export function convertMetricNameToDegrees(metricName: string): string {
  return metricName.replace(' (rad)', ' (°)').replace('[rad]', '[°]');
}

/**
 * Processes data to convert radian values to degrees
 * @param data - Array of data points
 * @param radianKeys - Array of keys that contain radian values
 * @returns Processed data with converted values
 */
export function convertRadianDataToDegrees(data: any[], radianKeys: string[]): any[] {
  return data.map(item => {
    const newItem = { ...item };
    radianKeys.forEach(key => {
      if (key in newItem && typeof newItem[key] === 'number') {
        // Create a new key with _deg suffix containing the converted value
        const degKey = key.replace('_rad', '_deg');
        newItem[degKey] = radiansToDegrees(newItem[key]);
      }
    });
    return newItem;
  });
}